// client/components/evaluate/IPillarTierBadge.tsx
// DC-1569 (start) — Tier indicator for I2 + I4 rule cards.
//
// Renders next to the verdict pill on I2 and I4 rule cards to signal which
// evidence path was credited:
//   - Tier 1 (Verified)  — green; `tag_iri`, `tag_iri_namespace_match`, `semantic_format`
//   - Tier 2 (Inferred)  — amber; `inferred_class_iri`, `inferred_class_iri_community_namespace_match`
//   - Tier 3 / unknown   — no badge rendered
//
// The percentage and target ontology name live in the rule's `description`
// text (composed by composePassDescription on the server). The badge is a
// quick at-a-glance tier indicator; the description carries the detail.
//
// Spec: Documentation/Development/Design Docs/Sprint 16/Triangulated_I_Pillar_Design.md
// Parent: DC-1564 · This component: DC-1569 (start)

import React from 'react';

interface IPillarTierBadgeProps {
  ruleId: string;
  method: string | undefined;
  status: 'PASS' | 'FAIL' | 'NOT_APPLICABLE';
}

const TIER_1_METHODS = new Set([
  'tag_iri',
  'tag_iri_namespace_match',
  'semantic_format',
]);

const TIER_2_METHODS = new Set([
  'inferred_class_iri',
  'inferred_class_iri_community_namespace_match',
]);

/**
 * Resolve the tier label for an I2 / I4 evaluator result. Returns null when
 * the rule isn't an I-pillar tier-aware rule, or when the result is a Tier 3
 * fail (no badge for fail).
 *
 * Exported so consumers (e.g. EvaluateRuleCard, which decides whether to
 * surface the Tier-2 mappings drill-down link) can derive the same tier
 * without duplicating the method-set membership rules.
 */
export function resolveIPillarTier(
  ruleId: string,
  method: string | undefined,
  status: string,
): 1 | 2 | null {
  if (ruleId !== 'I2' && ruleId !== 'I4') return null;
  if (status !== 'PASS') return null;
  if (!method) return null;
  if (TIER_1_METHODS.has(method)) return 1;
  if (TIER_2_METHODS.has(method)) return 2;
  return null;
}

export function IPillarTierBadge({ ruleId, method, status }: IPillarTierBadgeProps) {
  const tier = resolveIPillarTier(ruleId, method, status);
  if (tier === null) return null;

  const palette = tier === 1
    ? {
        bg: 'var(--tri-good-bg)',
        fg: 'var(--tri-good)',
        border: 'var(--tri-good)',
        label: 'Verified',
        title: 'Tier 1 (Declared) — vocabulary alignment confirmed by explicit class IRIs in Unity Catalog column tags.',
      }
    : {
        bg: 'var(--tri-soft-bg)',
        fg: 'var(--tri-soft)',
        border: 'var(--tri-soft)',
        label: 'Inferred',
        title: 'Tier 2 (Inferred) — vocabulary alignment inferred from Ontology Inference run against the journey’s target ontology. Tag matching columns in Unity Catalog to upgrade to Tier 1.',
      };

  return (
    <span
      title={palette.title}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 8px',
        borderRadius: 9999,
        background: palette.bg,
        color: palette.fg,
        fontSize: '0.7rem',
        fontWeight: 700,
        letterSpacing: '0.04em',
        border: `1px solid ${palette.border}`,
        flexShrink: 0,
      }}
    >
      {palette.label}
    </span>
  );
}
